/**
 * Types pour le système d'abonnements et de tokens
 * Basé sur la documentation API backend mise à jour
 */

// ===== ENUMS =====

export enum CustomerType {
  SME = 'sme',
  FINANCIAL_INSTITUTION = 'financial_institution'
}

export enum BillingPeriod {
  MONTHLY = 'monthly',
  ANNUAL = 'annual'
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  TRIAL = 'trial',
  PAST_DUE = 'past_due'
}

export enum TokenTransactionType {
  USAGE = 'usage',
  REFUND = 'refund',
  ADJUSTMENT = 'adjustment',
  EXPIRY = 'expiry',
  BONUS = 'bonus',
  ALLOCATION = 'allocation'
}

export enum TokenType {
  BONUS = 'bonus',
  REWARD = 'reward'
}

// Codes des fonctionnalités selon la documentation backend
export enum FeatureCode {
  // Gestion commerciale
  COMMERCIAL_MANAGEMENT = 'commercial_management',
  CUSTOMER_MANAGEMENT = 'customer_management',
  SALES_TRACKING = 'sales_tracking',
  INVENTORY_MANAGEMENT = 'inventory_management',
  
  // Comptabilité
  ACCOUNTING_BASIC = 'accounting_basic',
  ACCOUNTING_ADVANCED = 'accounting_advanced',
  FINANCIAL_REPORTS = 'financial_reports',
  TAX_MANAGEMENT = 'tax_management',
  
  // IA et tokens
  AI_CHAT_ASSISTANCE = 'ai_chat_assistance',
  DOCUMENT_ANALYSIS = 'document_analysis',
  PREDICTIVE_ANALYTICS = 'predictive_analytics',
  
  // Institutions financières
  PORTFOLIO_MANAGEMENT = 'portfolio_management',
  LEAD_PROSPECTING = 'lead_prospecting',
  COMPANY_PROSPECTING = 'company_prospecting',
  MULTI_USER_ACCESS = 'multi_user_access',
  USER_INVITATIONS = 'user_invitations',
  ROLE_MANAGEMENT = 'role_management',
  DEDICATED_SUPPORT = 'dedicated_support',
  PRIORITY_SUPPORT = 'priority_support',
  
  // Financement
  FINANCING_REQUESTS = 'financing_requests',
  CREDIT_SCORING = 'credit_scoring',
  RISK_ASSESSMENT = 'risk_assessment',
  
  // Technique
  API_ACCESS = 'api_access',
  THIRD_PARTY_INTEGRATIONS = 'third_party_integrations'
}

// ===== INTERFACES PRINCIPALES =====

/**
 * Allocation de tokens pour un plan
 */
export interface TokenAllocation {
  monthlyTokens: number;
  tokenRollover: boolean;
  maxRolloverMonths: number;
}

/**
 * Fonctionnalité d'un plan avec ses limites
 */
export interface PlanFeature {
  enabled: boolean;
  limit?: number;
  description?: string;
}

/**
 * Ensemble des fonctionnalités d'un plan
 */
export interface PlanFeatures {
  [key: string]: PlanFeature; // Indexé par FeatureCode
}

/**
 * Modèle de facturation d'un plan.
 *  - 'flat'     : prix fixe par période.
 *  - 'per_unit' : prix = unitPrice × quantity (souscription coopérative).
 */
export type PricingModel = 'flat' | 'per_unit';

/**
 * Plan d'abonnement selon la nouvelle structure backend
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  customerType: CustomerType;
  billingPeriod: BillingPeriod;

  // Tarification "flat"
  monthlyPriceUSD: number;
  annualPriceUSD: number;
  annualDiscountPercentage: number; // Réduction appliquée sur le prix annuel

  // Tarification "per_unit" (optionnelle — plans coopérative)
  pricingModel?: PricingModel;
  unitPrice?: number;     // ex : 5 (USD par coopérant/mois)
  unitName?: string;      // ex : 'coopérant'
  minUnits?: number;
  maxUnits?: number;
  /** Variant ciblé. Quand défini, n'apparaît qu'aux entités de ce variant. */
  targetVariant?: 'standard' | 'cooperative' | 'avec';

  // Allocation de tokens
  tokenAllocation: TokenAllocation;

  // Fonctionnalités incluses
  features: PlanFeatures;

  // Limites spécifiques (optionnel)
  limits?: Record<string, number | string>;

  // Métadonnées
  isPopular: boolean;
  isVisible: boolean; // Pour désactiver temporairement un plan
  sortOrder: number;
  tags: string[];

  // Metadata backend
  metadata?: {
    fromAdminService?: boolean;
    isDefaultPlan?: boolean;
    version?: number;
  };

  // ID de configuration (alternative à id)
  configId?: string;
}

/**
 * Configuration globale des plans depuis le backend
 */
export interface PlansConfig {
  baseCurrency: string;
  defaultAnnualDiscountPercentage: number;
  defaultTokenPricePerMillion: number;
  volumeDiscounts: VolumeDiscount[];
  defaultTokenRolloverMonths: number;
  maxTokenRolloverMonths: number;
  defaultFeatureLimits: {
    freeUsers: number;
    standardUsers: number;
    premiumUsers: number;
    enterpriseUsers: number;
  };
  trialPeriods: {
    sme: TrialPeriod;
    financial: TrialPeriod;
  };
}

/**
 * Réduction de volume sur les tokens
 */
export interface VolumeDiscount {
  minTokens: number;
  discountPercentage: number;
}

/**
 * Période d'essai
 */
export interface TrialPeriod {
  durationDays: number;
  includedTokens: number;
}

/**
 * Réponse complète de l'API /pricing/plans
 */
export interface PlansApiResponse {
  plans: SubscriptionPlan[];
  config: PlansConfig;
  source: 'default' | 'admin' | 'cache';
}

/**
 * Entrée de l'historique de report de tokens (customer-service, TokenBalanceResponseDto.rolloverHistory)
 */
export interface TokenRolloverEntry {
  period: string;
  rolledAmount: number;
  date: string;
  expiryDate?: string;
}

/**
 * Solde de tokens d'un client (CustomerTokenBalance)
 */
export interface CustomerTokenBalance {
  id: string;
  customerId: string;
  totalTokens: number;
  usedTokens: number;
  remainingTokens: number;
  monthlyAllocation: number;
  rolledOverTokens: number;
  bonusTokens: number;
  currentPeriod: string; // Format: YYYY-MM
  periodStartDate: Date;
  periodEndDate: Date;
  rolloverHistory: TokenRolloverEntry[];
  // Premium token fields
  premiumTokensTotal?: number;
  premiumTokensUsed?: number;
  premiumTokensRemaining?: number;
  planTokensIncluded?: number;
  planTokensUsed?: number;
  planTokensRemaining?: number;
  planTier?: string;
  canPurchasePremium?: boolean;
  purchasedTokens?: number;
  balance?: number;
  totalPurchased?: number;
}

/**
 * Sélection d'achat de tokens premium (via curseur)
 * Min: 1M tokens ($10) — Max: 50M tokens ($500)
 */
export interface TokenPurchasePackage {
  id: string;
  name: string;
  tokens: number;
  priceUSD: number;
  bonusTokens: number;
  isPopular?: boolean;
  customerTypes?: string[];
}

/**
 * Transaction de tokens
 */
export interface TokenTransaction {
  id: string;
  customerId: string;
  transactionType: TokenTransactionType;
  tokenAmount: number;
  balanceBefore: number;
  balanceAfter: number;
  relatedEntityId?: string;
  costUSD?: number;
  description: string;
  featureCode?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
}

/**
 * Abonnement actuel d'un utilisateur
 */
export interface Subscription {
  id: string;
  customerId: string;
  planId: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  startDate: string;
  endDate?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  trialEndsAt?: string;

  // Montant & devise
  amount?: string;        // "19.90"
  currency?: string;      // "USD"

  // Tokens (directement sur l'abonnement)
  tokensIncluded?: number;   // -1 = illimité
  tokensUsed?: number;
  tokensRemaining?: number;
  tokensRolloverAllowed?: boolean;
  subscriptionFeatures?: Record<string, unknown>;

  // Facturation
  nextBillingDate?: string;
  lastPaymentDate?: string | null;
  lastPaymentAmount?: string | null;
  nextPaymentAmount?: string | null;
  autoRenew?: boolean;
  billingContactEmail?: string | null;
  upgradeAvailable?: boolean;
  downgradeScheduled?: boolean;
  downgradeToPlanId?: string | null;
  downgradeEffectiveDate?: string | null;

  // Client associé (inclus dans GET /subscription/current)
  customer?: {
    id: string;
    name: string;
    email?: string;
    type?: string;
    address?: { street?: string; city?: string; country?: string };
    rccm?: string;
    taxId?: string;
    natId?: string;
    phone?: string;
  };

  // Méthode de paiement (peut être null côté backend)
  paymentMethod?: {
    type: 'card' | 'mobile';
    lastFour?: string;
    brand?: string;
    mobileMoneyProvider?: string;
    mobileMoneyNumber?: string;
  } | null;
  paymentReference?: string;

  // Métadonnées
  metadata?: Record<string, unknown>;
  suspendedAt?: string | null;
  suspensionReason?: string | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Informations sur la période de grâce
 * Utilisé dans EffectiveStatusResponse
 */
export interface GracePeriodInfo {
  inGracePeriod: boolean;
  daysRemaining: number;
  expiresAt?: string;
}

/**
 * Réponse du statut effectif de l'abonnement
 * GET /subscription/effective-status
 * 
 * Endpoint RECOMMANDÉ pour vérifier l'accès utilisateur.
 * Inclut la gestion du grace period et des downgrades.
 */
export interface EffectiveStatusResponse {
  subscription: Subscription | null;
  customer: {
    id: string;
    email: string;
    type: CustomerType;
  };
  isDowngraded: boolean;
  gracePeriod: GracePeriodInfo;
  planName: string;
  planTier: 'freemium' | 'standard' | 'premium' | 'enterprise';
  statusMessage: string;
}

/**
 * Détails de facturation pour créer un abonnement
 */
export interface BillingDetails {
  name: string;
  email: string;
  address: {
    line1: string;
    city: string;
    postalCode: string;
    country: string;
  };
}

/**
 * Méthode de paiement pour créer un abonnement
 */
export interface PaymentMethod {
  type: 'mobile' | 'card';
  // Mobile Money
  mobileMoneyProvider?: string;
  mobileMoneyNumber?: string;
  mobilePin?: string;
  // Carte bancaire
  cardNumber?: string;
  expiryMonth?: number;
  expiryYear?: number;
  cvc?: string;
  name?: string;
  cardToken?: string;
}

/**
 * Données pour créer un nouvel abonnement
 */
export interface CreateSubscriptionData {
  planId: string;
  billingPeriod?: BillingPeriod; // ✨ Optionnel pour Freemium
  paymentMethod: PaymentMethod;
  billingDetails?: BillingDetails; // ✨ Optionnel pour les plans gratuits
  /**
   * Nombre d'unités souscrites — uniquement pour les plans `per_unit`
   * (coopératives : nombre de coopérants). Ignoré pour les plans flat.
   * Lu depuis `sessionStorage.cooperative_member_count` au moment de
   * l'achat (Pricing.tsx → /register?variant=cooperative → handlePurchase).
   */
  quantity?: number;
}



// ===== RÉPONSES API =====

/**
 * Réponse pour la liste des plans d'abonnement
 */
export interface GetPlansResponse {
  plans: SubscriptionPlan[];
  config?: PlansConfig;
  source?: string;
}



/**
 * Réponse pour le solde de tokens
 */
export interface GetTokenBalanceResponse {
  balance: CustomerTokenBalance;
}

/**
 * Réponse pour l'historique des transactions de tokens
 */
export interface GetTokenTransactionsResponse {
  transactions: TokenTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Réponse pour l'abonnement actuel
 */
export interface GetSubscriptionResponse {
  subscription: Subscription | null;
}

// ===== FILTRES ET PARAMÈTRES =====

/**
 * Paramètres de requête pour récupérer les plans
 */
export interface GetPlansParams {
  customerType?: CustomerType;
  billingPeriod?: BillingPeriod;
}

/**
 * Paramètres de requête pour l'historique des tokens
 */
export interface GetTokenTransactionsParams {
  page?: number;
  limit?: number;
  transactionType?: TokenTransactionType;
  startDate?: string;
  endDate?: string;
}

// ===== TYPES DE COMPATIBILITÉ =====

/**
 * Type de compatibilité avec l'ancien système
 * @deprecated Utiliser SubscriptionPlan à la place
 */
export interface LegacyPricingPlan {
  name: string;
  description: string;
  price: string | number;
  features: string[];
}

/**
 * Conversion d'un SubscriptionPlan vers l'ancien format
 */
export function convertToLegacyPlan(plan: SubscriptionPlan): LegacyPricingPlan {
  return {
    name: plan.name,
    description: plan.description,
    price: plan.billingPeriod === BillingPeriod.MONTHLY ? plan.monthlyPriceUSD : plan.annualPriceUSD,
    features: Object.entries(plan.features)
      .filter((entry) => entry[1].enabled)
      .map(([code, feature]) => feature.description || code)
  };
}

// ===== HELPERS ET CONSTANTES =====

/**
 * Labels lisibles pour les codes de fonctionnalités
 * ✨ Mis à jour avec les nouvelles features du backend
 */
export const FEATURE_LABELS: Record<string, string> = {
  // === PME: Gestion commerciale ===
  accounting_entries_monthly: 'Écritures comptables',
  invoices_monthly: 'Factures',
  products_limit: 'Produits',
  branches_limit: 'Succursales',
  pos_limit: 'Points de vente',
  
  // === PME: Utilisateurs et accès ===
  users_limit: 'Utilisateurs',
  multiUserAccess: 'Multi-utilisateurs',
  
  // === PME: Intelligence artificielle ===
  ai_messages_monthly: 'Messages IA Adha',
  adha_contexts_limit: 'Contextes IA',
  
  // === PME: Stockage et exports ===
  storage_gb: 'Stockage',
  data_exports_monthly: 'Exports de données',
  api_calls_daily: 'Appels API',
  
  // === PME: Financement ===
  credit_score_access: 'Scoring de crédit',
  financing_access: 'Accès financement',
  financing_max_amount_usd: 'Montant max financement',
  financing_max_maturity_months: 'Maturité max financement',
  human_expertise_access: 'Expertise humaine',
  
  // === Institutions: Portefeuille ===
  portfolio_companies_limit: 'Entreprises en portefeuille',
  portfolios_limit: 'Portefeuilles',
  portfolio_volume_usd: 'Volume portefeuille',
  
  // === Institutions: Agences et zones ===
  agencies_limit: 'Agences',
  coverage_zones_limit: 'Zones de couverture',
  
  // === Institutions: Analyse de risque ===
  risk_assessments_monthly: 'Évaluations de risque',
  credit_applications_monthly: 'Demandes de crédit',
  adha_advanced_analytics: 'Analytics avancés IA',
  
  // === Options génériques ===
  apiAccess: 'Accès API',
  dataExport: 'Export de données',
  customReporting: 'Rapports personnalisés',
  advancedAnalytics: 'Analytics avancés',
  customIntegrations: 'Intégrations personnalisées',
  prioritySupport: 'Support prioritaire',
  dedicatedAccountManager: 'Account Manager dédié',
  whiteLabeling: 'Marque blanche'
};

/**
 * Catégories de fonctionnalités pour l'affichage groupé
 * ✨ Mis à jour avec les nouvelles features du backend par segment
 */
export const FEATURE_CATEGORIES: Record<string, { label: string; features: string[] }> = {
  // === CATÉGORIES PME ===
  commercial: {
    label: 'Gestion Commerciale',
    features: ['accounting_entries_monthly', 'invoices_monthly', 'products_limit', 'branches_limit', 'pos_limit']
  },
  users: {
    label: 'Utilisateurs & Accès',
    features: ['users_limit', 'multiUserAccess']
  },
  ai: {
    label: 'Intelligence Artificielle',
    features: ['ai_messages_monthly', 'adha_contexts_limit']
  },
  storage: {
    label: 'Stockage & Exports',
    features: ['storage_gb', 'data_exports_monthly', 'api_calls_daily']
  },
  financing: {
    label: 'Financement',
    features: ['credit_score_access', 'financing_access', 'financing_max_amount_usd', 'financing_max_maturity_months', 'human_expertise_access']
  },
  
  // === CATÉGORIES INSTITUTIONS ===
  portfolio: {
    label: 'Gestion de Portefeuille',
    features: ['portfolio_companies_limit', 'portfolios_limit', 'portfolio_volume_usd']
  },
  agencies: {
    label: 'Réseau & Couverture',
    features: ['agencies_limit', 'coverage_zones_limit']
  },
  riskAnalysis: {
    label: 'Analyse de Risque',
    features: ['risk_assessments_monthly', 'credit_applications_monthly', 'adha_advanced_analytics']
  },
  
  // === CATÉGORIES COMMUNES ===
  support: {
    label: 'Support & Services',
    features: ['prioritySupport', 'dedicatedAccountManager', 'human_expertise_access']
  },
  technical: {
    label: 'Options Techniques',
    features: ['apiAccess', 'customIntegrations', 'customReporting', 'advancedAnalytics', 'whiteLabeling']
  }
};

/**
 * Obtenir le label lisible d'une fonctionnalité
 */
export function getFeatureLabel(featureCode: string): string {
  return FEATURE_LABELS[featureCode] || featureCode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Templates de descriptions explicites pour chaque fonctionnalité
 * Utilise {limit} comme placeholder pour la valeur limite
 * ✨ Permet d'avoir des descriptions contextuelles et compréhensibles
 */
export const FEATURE_DESCRIPTION_TEMPLATES: Record<string, {
  withLimit: string;       // Template quand il y a une limite (ex: "{limit} Écritures/mois")
  unlimited: string;       // Texte quand limit = -1
  noLimit: string;         // Texte quand enabled mais pas de limite
  disabled: string;        // Texte quand disabled
  unit?: string;           // Unité optionnelle pour le formatage
}> = {
  // === PME: Gestion Commerciale ===
  accounting_entries_monthly: {
    withLimit: '{limit} Écritures comptables/mois',
    unlimited: 'Écritures comptables illimitées',
    noLimit: 'Comptabilité incluse',
    disabled: 'Comptabilité non incluse'
  },
  invoices_monthly: {
    withLimit: '{limit} Factures/mois',
    unlimited: 'Facturation illimitée',
    noLimit: 'Facturation incluse',
    disabled: 'Facturation non incluse'
  },
  products_limit: {
    withLimit: '{limit} Produits max',
    unlimited: 'Produits illimités',
    noLimit: 'Gestion produits incluse',
    disabled: 'Gestion produits non incluse'
  },
  branches_limit: {
    withLimit: '{limit} Succursale(s)',
    unlimited: 'Succursales illimitées',
    noLimit: 'Multi-succursales',
    disabled: 'Une seule succursale'
  },
  pos_limit: {
    withLimit: '{limit} Point(s) de vente',
    unlimited: 'Points de vente illimités',
    noLimit: 'Multi-POS inclus',
    disabled: 'Un seul point de vente'
  },
  
  // === Utilisateurs ===
  users_limit: {
    withLimit: '{limit} Utilisateur(s)',
    unlimited: 'Utilisateurs illimités',
    noLimit: 'Multi-utilisateurs',
    disabled: '1 utilisateur uniquement'
  },
  
  // === IA Adha ===
  ai_messages_monthly: {
    withLimit: '{limit} Messages IA Adha/mois',
    unlimited: 'Assistant IA Adha illimité',
    noLimit: 'Assistant IA Adha inclus',
    disabled: 'Assistant IA non inclus'
  },
  adha_contexts_limit: {
    withLimit: '{limit} Contexte(s) IA personnalisés',
    unlimited: 'Contextes IA personnalisés illimités',
    noLimit: 'Contextes IA personnalisés',
    disabled: 'Contexte IA basique uniquement'
  },
  adha_advanced_analytics: {
    withLimit: 'Analytics IA avancés inclus',
    unlimited: 'Analytics IA avancés inclus',
    noLimit: 'Analytics IA avancés inclus',
    disabled: 'Analytics IA basiques'
  },
  
  // === Stockage & Exports ===
  storage_gb: {
    withLimit: '{limit} Go de stockage cloud',
    unlimited: 'Stockage cloud illimité',
    noLimit: 'Stockage cloud inclus',
    disabled: 'Stockage limité',
    unit: 'Go'
  },
  data_exports_monthly: {
    withLimit: '{limit} Export(s) de données/mois',
    unlimited: 'Exports de données illimités',
    noLimit: 'Exports de données inclus',
    disabled: 'Exports non disponibles'
  },
  api_calls_daily: {
    withLimit: '{limit} Appels API/jour',
    unlimited: 'Accès API illimité',
    noLimit: 'Accès API inclus',
    disabled: 'Accès API non disponible'
  },
  
  // === Financement PME ===
  credit_score_access: {
    withLimit: 'Scoring de crédit',
    unlimited: 'Scoring de crédit complet',
    noLimit: 'Scoring de crédit inclus',
    disabled: 'Scoring de crédit non inclus'
  },
  financing_access: {
    withLimit: 'Accès financement',
    unlimited: 'Accès financement complet',
    noLimit: 'Demandes de financement',
    disabled: 'Financement non disponible'
  },
  financing_max_amount_usd: {
    withLimit: 'Financement jusqu\'à {limit} USD',
    unlimited: 'Montant de financement illimité',
    noLimit: 'Financement disponible',
    disabled: 'Financement non disponible',
    unit: 'USD'
  },
  financing_max_maturity_months: {
    withLimit: 'Maturité jusqu\'à {limit} mois',
    unlimited: 'Maturité flexible',
    noLimit: 'Maturité flexible',
    disabled: 'Non applicable'
  },
  human_expertise_access: {
    withLimit: 'Expertise humaine incluse',
    unlimited: 'Expertise humaine illimitée',
    noLimit: 'Accès experts Wanzo',
    disabled: 'Expertise IA uniquement'
  },
  
  // === Institutions: Portefeuille ===
  portfolio_companies_limit: {
    withLimit: '{limit} Entreprises en portefeuille',
    unlimited: 'Entreprises en portefeuille illimitées',
    noLimit: 'Gestion portefeuille entreprises',
    disabled: 'Portefeuille entreprises limité'
  },
  portfolios_limit: {
    withLimit: '{limit} Portefeuille(s) de suivi',
    unlimited: 'Portefeuilles de suivi illimités',
    noLimit: 'Multi-portefeuilles inclus',
    disabled: 'Un seul portefeuille de suivi'
  },
  portfolio_volume_usd: {
    withLimit: 'Volume géré jusqu\'à {limit} USD',
    unlimited: 'Volume de portefeuille illimité',
    noLimit: 'Volume géré flexible',
    disabled: 'Volume de portefeuille limité',
    unit: 'USD'
  },
  
  // === Institutions: Agences ===
  agencies_limit: {
    withLimit: '{limit} Agence(s) connectée(s)',
    unlimited: 'Réseau d\'agences illimité',
    noLimit: 'Multi-agences inclus',
    disabled: 'Une seule agence'
  },
  coverage_zones_limit: {
    withLimit: '{limit} Zone(s) de couverture géographique',
    unlimited: 'Couverture géographique illimitée',
    noLimit: 'Multi-zones inclus',
    disabled: 'Zone géographique unique'
  },
  
  // === Institutions: Risque ===
  risk_assessments_monthly: {
    withLimit: '{limit} Évaluations de risque/mois',
    unlimited: 'Évaluations de risque PME illimitées',
    noLimit: 'Évaluations de risque incluses',
    disabled: 'Évaluations de risque non disponibles'
  },
  credit_applications_monthly: {
    withLimit: '{limit} Demandes de crédit traitées/mois',
    unlimited: 'Traitement demandes de crédit illimité',
    noLimit: 'Traitement des demandes inclus',
    disabled: 'Traitement des demandes non disponible'
  }
};

/**
 * Formater une fonctionnalité pour l'affichage UI
 * ✨ Utilise les templates explicites pour des descriptions compréhensibles
 */
export function formatFeatureDisplay(featureCode: string, feature: PlanFeature): {
  label: string;
  description: string;
  enabled: boolean;
  hasLimit: boolean;
} {
  const label = getFeatureLabel(featureCode);
  
  // Si le backend a fourni une description, l'utiliser
  if (feature.description) {
    return {
      label,
      description: feature.description,
      enabled: feature.enabled,
      hasLimit: feature.limit !== undefined
    };
  }
  
  // Sinon, utiliser les templates explicites
  const template = FEATURE_DESCRIPTION_TEMPLATES[featureCode];
  let description = '';
  
  if (template) {
    // Feature désactivée
    if (!feature.enabled) {
      description = template.disabled;
    }
    // Feature avec limite
    else if (feature.limit !== undefined) {
      const limit = feature.limit;
      
      if (limit === -1) {
        // Illimité
        description = template.unlimited;
      } else if (limit === 0) {
        // Non disponible (enabled mais limit=0)
        description = template.disabled;
      } else {
        // Valeur numérique - formater le nombre si nécessaire
        let formattedLimit: string;
        if (template.unit === 'USD' && limit >= 1000) {
          // Formater les montants USD avec séparateurs
          formattedLimit = limit.toLocaleString('fr-FR');
        } else {
          formattedLimit = limit.toString();
        }
        description = template.withLimit.replace('{limit}', formattedLimit);
      }
    }
    // Feature activée sans limite explicite
    else {
      description = template.noLimit;
    }
  } else {
    // Fallback pour les features sans template (ancien comportement amélioré)
    if (!feature.enabled) {
      description = 'Non inclus';
    } else if (feature.limit !== undefined) {
      const limit = feature.limit;
      if (limit === -1) {
        description = `${label} illimité`;
      } else if (limit === 0) {
        description = 'Non disponible';
      } else {
        // Créer une description avec le label
        if (featureCode.includes('_monthly')) {
          description = `${limit} ${label}/mois`;
        } else if (featureCode.includes('_daily')) {
          description = `${limit} ${label}/jour`;
        } else if (featureCode.includes('_gb') || featureCode === 'storage_gb') {
          description = `${limit} Go de stockage`;
        } else if (featureCode.includes('_usd') || featureCode.includes('amount')) {
          description = `${limit.toLocaleString('fr-FR')} USD max`;
        } else if (featureCode.includes('_limit')) {
          description = `${limit} ${label} max`;
        } else {
          description = `${limit} ${label}`;
        }
      }
    } else {
      description = `${label} inclus`;
    }
  }
  
  return {
    label,
    description,
    enabled: feature.enabled,
    hasLimit: feature.limit !== undefined
  };
}

/**
 * Obtenir les fonctionnalités principales (pertinentes pour l'affichage)
 * Filtre pour afficher ce qui est compréhensible par l'utilisateur
 * ✨ Mis à jour avec les nouvelles features du backend
 */
export function getRelevantFeatures(features: PlanFeatures, maxCount: number = 6): Array<{
  code: string;
  label: string;
  description: string;
  enabled: boolean;
}> {
  // Ordre de priorité pour l'affichage des fonctionnalités PME
  const smePriorityOrder = [
    'accounting_entries_monthly',
    'invoices_monthly',
    'products_limit',
    'users_limit',
    'ai_messages_monthly',
    'credit_score_access',
    'financing_access',
    'financing_max_amount_usd',
    'storage_gb',
    'branches_limit',
    'pos_limit',
    'adha_contexts_limit',
    'human_expertise_access',
    'data_exports_monthly',
    'api_calls_daily'
  ];
  
  // Ordre de priorité pour l'affichage des fonctionnalités Institutions
  const institutionPriorityOrder = [
    'portfolio_companies_limit',
    'portfolios_limit',
    'agencies_limit',
    'users_limit',
    'portfolio_volume_usd',
    'risk_assessments_monthly',
    'credit_applications_monthly',
    'ai_messages_monthly',
    'adha_advanced_analytics',
    'coverage_zones_limit',
    'adha_contexts_limit',
    'storage_gb',
    'human_expertise_access',
    'data_exports_monthly',
    'api_calls_daily'
  ];
  
  // Déterminer le type de client basé sur les features présentes
  const isInstitution = features['portfolio_companies_limit'] || features['agencies_limit'] || features['portfolios_limit'];
  const priorityOrder = isInstitution ? institutionPriorityOrder : smePriorityOrder;
  
  const allFeatures = Object.entries(features)
    .filter(([code]) => !['tags', 'customerType', 'tokenAllocation'].includes(code)) // Exclure les métadonnées
    .map(([code, feature]) => ({
      code,
      ...formatFeatureDisplay(code, feature)
    }))
    .filter(f => f.enabled); // Ne montrer que les features activées par défaut
  
  // Trier par priorité
  allFeatures.sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a.code);
    const bIndex = priorityOrder.indexOf(b.code);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
  
  return allFeatures.slice(0, maxCount);
}

/**
 * Formater le montant de messages/tokens IA pour l'affichage
 * ✨ Mis à jour pour le nouveau système (messages IA au lieu de tokens)
 * @param tokens - Nombre de messages IA (-1 = illimité)
 */
export function formatTokenAmount(tokens: number): string {
  // -1 signifie illimité
  if (tokens === -1) {
    return 'Illimité';
  }
  
  // 0 signifie non disponible
  if (tokens === 0) {
    return '0';
  }
  
  // Formatage intelligent selon l'ordre de grandeur
  if (tokens >= 1000000) {
    const millions = tokens / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  } else if (tokens >= 1000) {
    const thousands = tokens / 1000;
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }
  
  return tokens.toString();
}
